<script setup lang="ts">
import { onMounted, onUnmounted, ref } from 'vue';
import LottieAnimation from '@/components/Common/LottieAnimation.vue';
import AIStarAnimationData from '@/assets/json/ff-ai-star.json';

interface Props {
  width?: string;
  height?: string;
  speed?: number;
  showText?: boolean;
  textCycleDuration?: number;
}

const props = withDefaults(defineProps<Props>(), {
  width: '40px',
  height: '40px',
  speed: 1,
  showText: true,
  textCycleDuration: 3000,
});

const emit = defineEmits<{
  (e: 'animation:complete'): void;
  (e: 'animation:loaded'): void;
}>();

const messages = [
  'Scanning for best matches',
  'Reviewing profiles and skills',
  'Assessing candidate fit',
  'Evaluating key experience',
  'Prioritizing best matches',
];

const currentMessageIndex = ref(0);
const intervalId = ref<number | null>(null);

const handleComplete = () => {
  emit('animation:complete');
};

const handleLoaded = () => {
  emit('animation:loaded');
};

onMounted(() => {
  if (props.showText) {
    intervalId.value = window.setInterval(() => {
      currentMessageIndex.value = (currentMessageIndex.value + 1) % messages.length;
    }, props.textCycleDuration);
  }
});

onUnmounted(() => {
  if (intervalId.value) {
    clearInterval(intervalId.value);
  }
});
</script>

<template>
  <div class="flex items-center gap-3">
    <LottieAnimation
      :animation-data="AIStarAnimationData"
      :loop="true"
      :autoplay="true"
      :width="width"
      :height="height"
      :speed="speed"
      @animation:complete="handleComplete"
      @animation:loaded="handleLoaded"
    />
    <p v-if="showText" class="animated-dots text-purple-600 text-base font-bold">
      {{ messages[currentMessageIndex] }}
    </p>
  </div>
</template>

<style scoped>
.animated-dots::after {
  content: '';
  animation: dots 1.5s steps(3, end) infinite;
}

@keyframes dots {
  0% {
    content: '';
  }
  25% {
    content: '.';
  }
  50% {
    content: '..';
  }
  75% {
    content: '...';
  }
  100% {
    content: '...';
  }
}
</style>
